import {
  IConversation,
  IDataSource,
  QUERIES,
  dataSourcesInfo,
  getDataSourceSummary,
} from "@dataone/lib";
import { Divider, Popover, Skeleton, Typography } from "antd";
import { styled } from "styled-components";
import { useEffect, useState } from "react";
import {
  DataSetIcon,
  EntrepriseConnectorIcon,
  FileIcon,
  SummaryIcon,
  UnderlineIcon,
} from "@dataone/icons";
import { useQuery } from "@tanstack/react-query";

interface ChatDatasourceHeaderProps {
  children?: React.ReactNode;
  selectedDatasources: dataSourcesInfo[];
  datasources?: IDataSource[];
  selectedConversation?: IConversation;
  setSelectedDatasources: React.Dispatch<
    React.SetStateAction<dataSourcesInfo[]>
  >;
}

const DataSourceSummary = (id: string) => {
  const { data, isLoading } = useQuery({
    queryKey: [QUERIES.DOC_SUMMARY, id],
    queryFn: () => getDataSourceSummary(id),
  });
  return (
    <StyledDataSourceSummary>
      <SummaryIcon />
      <div style={{ width: "100%" }}>
        <h4>Summary</h4>
        {isLoading ? (
          <>
            <Skeleton.Input active block size="small" />
            <Skeleton.Input active block size="small" />
          </>
        ) : (
          <p>{data?.doc_summary || "No summary found"}</p>
        )}
      </div>
    </StyledDataSourceSummary>
  );
};

const ChatDatasourceHeader = ({
  selectedDatasources,
  datasources,
  selectedConversation,
  setSelectedDatasources,
}: ChatDatasourceHeaderProps) => {
  const [hoveredDatasource, setHoveredDatasource] = useState<string>();

  useEffect(() => {
    if (selectedConversation && selectedConversation?.dataSources) {
      const updatedDatasources: dataSourcesInfo[] =
        datasources
          ?.filter((d) =>
            selectedConversation?.dataSources?.includes(d?.id || "")
          )
          .map(({ id, dataSourceType, displayName }) => ({
            id,
            type: dataSourceType,
            name: displayName,
          })) || [];
      setSelectedDatasources(updatedDatasources || []);
    }
  }, [selectedConversation]);

  const handleRemoveDatasource = (datasourceId: string) => {
    const updatedDatasources = selectedDatasources.filter(
      (datasource) => datasource.id !== datasourceId
    );
    setHoveredDatasource(undefined);
    setSelectedDatasources(updatedDatasources);
  };

  return (
    <>
      {selectedDatasources.length > 0 && (
        <StyledChatDatasourceHeader>
          <StyledDatasourceHeader>
            <>
              {selectedDatasources.map((datasource) => {
                return (
                  <Popover
                    content={() => DataSourceSummary(datasource.id!)}
                    title=""
                    placement="bottomLeft"
                  >
                    <StyledDatasourceDetails
                      key={datasource.id}
                      onMouseOver={() => setHoveredDatasource(datasource.id)}
                      onMouseLeave={() => setHoveredDatasource(undefined)}
                    >
                      <StyledIcons>
                        {datasource.type === "ENTERPRISE_CONNECTOR" ? (
                          <EntrepriseConnectorIcon />
                        ) : datasource.type === "FILE" ? (
                          <FileIcon />
                        ) : (
                          <DataSetIcon />
                        )}
                      </StyledIcons>
                      <StyledDatasourceTitle
                        $hovered={datasource.id === hoveredDatasource}
                      >
                        <Typography.Paragraph
                          editable={false}
                          style={{
                            maxWidth: "85px",
                            margin: "0px",
                          }}
                          ellipsis={{
                            tooltip: `${datasource.name}`,
                          }}
                        >
                          {datasource.name}
                        </Typography.Paragraph>
                        {/* <span className="datasource-title">
                        {datasource.name}{" "}
                      </span> */}
                      </StyledDatasourceTitle>
                      {datasource.id === hoveredDatasource && (
                        <StyledDeleteIcon
                          onClick={() => handleRemoveDatasource(datasource.id!)}
                        >
                          <UnderlineIcon />
                        </StyledDeleteIcon>
                      )}
                    </StyledDatasourceDetails>
                  </Popover>
                );
              })}
            </>
          </StyledDatasourceHeader>
          <Divider />
        </StyledChatDatasourceHeader>
      )}
    </>
  );
};

export default ChatDatasourceHeader;

const StyledDataSourceSummary = styled.div`
  width: 500px;
  padding: 10px 18px;
  display: flex;
  gap: 15px;
  align-items: start;

  .ant-skeleton {
    margin-bottom: 10px;
  }

  > svg {
    width: 28px;
  }

  h4 {
    margin-top: 2px;
    margin-bottom: 10px;
  }
  p {
    margin: 0;
  }
`;

const StyledChatDatasourceHeader = styled.div`
  position: relative;
  .ant-divider-horizontal {
    min-width: auto;
    background-color: #fff !important;
    height: 2px !important;
    stroke-width: 2px;
    stroke: #fff;
    filter: drop-shadow(0px 2px 4px rgba(73, 116, 246, 0.3));
    margin: 0px !important;
  }
`;

const StyledDatasourceHeader = styled.div`
  padding: 14px 0px;
  display: flex;
  gap: 15px;
  overflow-x: auto;
  overflow-y: hidden;
  height: 82px;
  align-items: center;
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #dbe4fd;
    border-radius: 7px;
  }

  &::-webkit-scrollbar-button {
    display: block;
    height: 16px;
    width: 16px;
    background-color: transparent;
    border: none;
  }
`;

const StyledDatasourceDetails = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
  height: 52px;
  img {
    position: absolute;
    height: 50px;
  }
`;

const StyledDatasourceTitle = styled.div<{ $hovered: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 0 0 0 58px;
  background: transparent;
  justify-content: center;
  height: 50px;
  width: 150px;
  ${(p) => p.$hovered && "background:#ffffff; border-radius: 30px;"}
  .datasource-title {
    color: #040726;
    font-size: 13px;
    font-weight: 400;
    letter-spacing: -0.18px;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 96%;
    text-wrap: nowrap;
  }
`;
const StyledIcons = styled.div`
  cursor: pointer;
  font-size: 16px;
  color: #4974f6;
  padding: 17px;
  background: #fff;
  border-radius: 50%;
  display: flex;
  position: absolute;
`;
const StyledDeleteIcon = styled.div`
  top: -6px;
  right: 2px;
  position: absolute;
  color: #ff0000;
  padding: 5px;
  background: rgb(255, 0, 0, 0.4);
  border-radius: 21px;
  height: 20px;
  font-size: 10px;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
`;
